import PublicIcon from '@mui/icons-material/Public';
import { Box, CircularProgress, Skeleton, TextField, Typography, useTheme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Alert from '/@/components/bayer/alert';
import SingleDateSlider from '/@/components/range-date-selector';
import colors from '/@/components/theme-provider/colors';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useFrostRiskGdus } from '/@/hooks/frost-risk';
import { useZones } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { useGetFieldZone } from '/@/store/fields/apiProviders';
import { getSessionCountry } from '/@/store/session/selectors';
import { formatDate } from '/@/utils';
import { assignYearsToDateRange } from '/@/utils/date';

export const getColor = (index = 0) => {
    const lineColors = [
        '#624963',
        '#00BCFF',
        '#FF3162',
        '#2ECC71',
        '#F39C12',
        '#8E44AD',
        '#C0392B',
        '#1ABC9C',
        '#D35400',
        '#333333',
        '#16A085',
        '#F1C40F',
        '#A93226',
        '#D4AC0D',
        '#808080',
        '#FFCC9A',
        '#48C9B0',
        '#FF99CC',
        '#99FF99',
        '#993399',
        '#FFCC00',
        '#FF66CC',
        '#99FF33',
        '#A9A9A9',
        '#FFA07A',
        '#F0FFF0',
        '#A0522D',
        '#C71585',
        '#808000',
        '#000080',
        '#FFD700',
        '#483D8B',
        '#FF6347',
    ];
    const safeIndex = Number.isFinite(index) ? index : 0;
    const newIndex = safeIndex > lineColors.length - 1 ? safeIndex % lineColors.length : safeIndex;
    return lineColors[newIndex];
};

type GDUForecastProps = {
    isHome?: boolean;
};

const GDUForecast = ({ isHome = false }: GDUForecastProps) => {
    const theme = useTheme();

    const country = useSelector(getSessionCountry);
    const [locationName, setLocationName] = useState('');
    const debouncedLocationName = useDebounce(locationName, 300);

    type LocationOption = {
        location: string;
        id: string;
        lat: number;
        lng: number;
    };

    const [locations, setLocations] = useState<LocationOption[]>([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [defaultDate, setDefaultDate] = useState<string | undefined>(undefined);
    const [activeLocation, setActiveLocation] = useState<LocationOption | undefined>(undefined);

    const zoneGDUQuery = useZones(
        {
            type: 'gdu',
            lat: activeLocation?.lat,
            lng: activeLocation?.lng,
        },
        { enabled: !!activeLocation }
    );

    const { data: zones } = useGetFieldZone({
        lat: activeLocation?.lat,
        lng: activeLocation?.lng,
        crop: 'corn',
    });
    const frosRiskGDUsQuery = useFrostRiskGdus({
        lat: activeLocation?.lat,
        lng: activeLocation?.lng,
        date: defaultDate,
        locationId: zoneGDUQuery.data?.[0]?.id,
    });

    useEffect(() => {
        if (zones) {
            if (!zones[0] || !zones[0].defaultPlantingDate || !zones[0].minDate || !zones[0].maxDate) return;
            const { defaultPlantingDate } =
                assignYearsToDateRange(zones[0].minDate, zones[0].maxDate, zones[0].defaultPlantingDate) || {};

            setDefaultDate(defaultPlantingDate);
        }
    }, [zones]);

    useEffect(() => {
        if (!debouncedLocationName || debouncedLocationName.length <= 1) {
            setLocations([]);
            setLocationsLoading(false);
            return;
        }

        if (typeof google === 'undefined' || !google.maps) {
            setLocations([]);
            setLocationsLoading(false);
            return;
        }

        let active = true;
        setLocationsLoading(true);

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {
                // Force suggestions to country code
                address: debouncedLocationName,
                region: country?.code,
                componentRestrictions: { country: country?.code },
            },
            (results, status) => {
                if (!active) return;

                if (status === 'OK' && results) {
                    const mapped: LocationOption[] = results
                        .slice(0, 5)
                        .filter((r) => !!r.geometry && !!r.geometry.location)
                        .map((r, index) => ({
                            location: r.formatted_address,
                            id: r.place_id || `${index}`,
                            lat: r.geometry!.location!.lat(),
                            lng: r.geometry!.location!.lng(),
                        }));
                    setLocations(mapped);
                } else {
                    setLocations([]);
                }

                setLocationsLoading(false);
            }
        );

        return () => {
            active = false;
        };
    }, [country?.code, debouncedLocationName]);

    function getMinAndMaxDates(data: any[]) {
        if (!data || !data?.length) return;

        const { minDate, maxDate } =
            assignYearsToDateRange(data[0].minDate, data[0].maxDate, data[0].defaultPlantingDate) || {};
        if (!minDate || !maxDate) return;

        const dates = data.reduce(
            (acc: any, item: any) => {
                // Use smart year assignment for each zone
                const assignedDates = assignYearsToDateRange(item.minDate, item.maxDate);

                if (!assignedDates) return acc;

                const itemMinDate = new Date(assignedDates.minDate);
                const itemMaxDate = new Date(assignedDates.maxDate);

                if (!itemMinDate || !itemMaxDate) return acc;

                acc.minDate = itemMinDate < acc.minDate ? itemMinDate : acc.minDate;
                acc.maxDate = itemMaxDate > acc.maxDate ? itemMaxDate : acc.maxDate;

                return acc;
            },
            {
                minDate: new Date(minDate),
                maxDate: new Date(maxDate),
            }
        );

        return {
            minDate: dates.minDate.toISOString().split('T')[0],
            maxDate: dates.maxDate.toISOString().split('T')[0],
        };
    }

    const dates = getMinAndMaxDates(zones);

    return (
        <Box
            sx={{
                m: isHome ? 0 : 2,
                pb: 4,
                mx: {
                    xs: isHome ? 0 : 4,
                    sm: isHome ? 0 : 6,
                    md: isHome ? 0 : 8,
                },
                pointerEvents: 'auto',
            }}
        >
            <Typography variant='h5' fontWeight='bold' color='primary'>
                {_('Pronóstico de GDUs')}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    my: 1,
                    mt: 3,
                }}
            >
                <PublicIcon fontSize='large' sx={{ mr: 1 }} color='primary' />
                <Typography variant='h6' color='primary'>
                    {_('Localidades')}
                </Typography>
            </Box>

            <Box sx={{ my: 2 }}>
                <Typography variant='body1' color='primary'>
                    {_('Selecciona cuál quieres ver en el gráfico')}
                </Typography>
            </Box>

            <Autocomplete
                value={activeLocation || null}
                options={locations}
                onChange={(_, option) => {
                    if (!option) {
                        return;
                    }
                    setActiveLocation(option);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.location ?? ''}
                noOptionsText={
                    debouncedLocationName.trim().length === 0
                        ? _('Escribí el nombre de una localidad')
                        : _('No hay localidades que coincidan con la búsqueda')
                }
                loading={locationsLoading}
                loadingText={_('Cargando')}
                filterOptions={(x) => x}
                onInputChange={(_event, newInputValue, reason) => {
                    if (reason !== 'reset') setLocationName(newInputValue);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={_('Localidades')}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {locationsLoading ? <CircularProgress color='inherit' size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                disableClearable
            />

            {!!zones && !!zones.length && defaultDate && (
                <>
                    <Typography color='primary' sx={{ p: { xs: 1, md: 3 }, mb: { xs: 2, md: 0 } }}>
                        {_('Ajusta tu fecha de siembra:')}
                    </Typography>

                    <SingleDateSlider
                        minDate={dates?.minDate}
                        maxDate={dates?.maxDate}
                        defaultDate={defaultDate}
                        setDefaultDate={setDefaultDate}
                    />
                </>
            )}

            {!activeLocation &&
            !frosRiskGDUsQuery.data &&
            !frosRiskGDUsQuery.isValidating ? null : frosRiskGDUsQuery.isValidating ? (
                <Skeleton
                    variant='rectangular'
                    sx={{
                        width: { xs: '100%', md: isHome ? '100%' : '80%' },
                        height: isHome ? 250 : 400,
                        mt: 2,
                        mb: 10,
                        mx: 'auto',
                    }}
                />
            ) : frosRiskGDUsQuery.data?.length === 0 && defaultDate !== undefined ? (
                <Alert severity='danger' sx={{ my: 2 }}>
                    {_('No contamos con datos suficientes para mostrar el gráfico')}
                </Alert>
            ) : (
                <Box
                    sx={{
                        width: { xs: '100%', md: isHome ? '100%' : '80%' },
                        height: isHome ? 250 : 400,
                        mt: 2,
                        mb: 10,
                        mx: 'auto',
                    }}
                >
                    <ResponsiveContainer>
                        <LineChart data={frosRiskGDUsQuery.data}>
                            <XAxis
                                dataKey='date'
                                type='category'
                                allowDuplicatedCategory={false}
                                label={{
                                    value: _('Fecha'),
                                    position: 'insideBottomRight',
                                    offset: 0,
                                    style: {
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: colors.gray.default,
                                    },
                                }}
                            />
                            <YAxis
                                dataKey='value'
                                label={{
                                    value: _('GDU / día'),
                                    position: 'insideLeft',
                                    angle: -90,
                                    offset: 0,
                                    style: {
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: colors.gray.default,
                                    },
                                }}
                            />
                            {defaultDate && (
                                <ReferenceLine
                                    x={formatDate(new Date(defaultDate))}
                                    stroke={theme.palette.primary.main}
                                    label={{
                                        position: 'insideRight',
                                        value: _('Fecha de siembra'),
                                        fill: theme.palette.primary.main,
                                        fontSize: 14,
                                    }}
                                />
                            )}
                            <CartesianGrid stroke='#eee' strokeDasharray='1' />
                            <Line
                                dataKey='value'
                                data={frosRiskGDUsQuery.data}
                                key={activeLocation?.location}
                                name={activeLocation?.location}
                                stroke={getColor(0)}
                            />

                            <Tooltip />
                        </LineChart>
                    </ResponsiveContainer>
                    <Box sx={{ my: 1, textAlign: 'center', pb: 2 }}>
                        <Typography variant='body2' color='primary.mui.inactive'>
                            {_('Fuente: The Weather Company')}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default GDUForecast;
